import React from 'react'
import { motion } from 'framer-motion'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

interface PaginationProps {
  currentPage: number
  totalPages: number
  totalItems: number
  itemsPerPage: number
  onPageChange: (page: number) => void
  onItemsPerPageChange?: (limit: number) => void
  isLoading?: boolean
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  isLoading = false,
}) => {
  const { t } = useTranslation()
  if (totalPages <= 1) {
    return null
  }

  // 페이지 번호 생성 로직
  const generatePageNumbers = () => {
    const pages: (number | string)[] = []
    const maxVisiblePages = 7

    if (totalPages <= maxVisiblePages) {
      // 총 페이지가 적으면 모든 페이지 표시
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // 많으면 현재 페이지 중심으로 표시
      if (currentPage <= 4) {
        // 처음 부분
        for (let i = 1; i <= 5; i++) {
          pages.push(i)
        }
        pages.push('...')
        pages.push(totalPages)
      } else if (currentPage >= totalPages - 3) {
        // 끝 부분
        pages.push(1)
        pages.push('...')
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i)
        }
      } else {
        // 중간 부분
        pages.push(1)
        pages.push('...')
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i)
        }
        pages.push('...')
        pages.push(totalPages)
      }
    }

    return pages
  }

  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalItems)

  return (
    <div className="border-t border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800 sm:px-6 3xl:px-8 3xl:py-4 7xl:px-12 7xl:py-6">
      <div className="flex flex-col items-center justify-between gap-4 sm:flex-row 3xl:gap-6">
        {/* 정보 표시 */}
        <div className="flex flex-col items-center gap-4 sm:flex-row 3xl:gap-6">
          <div className="text-sm text-gray-700 dark:text-gray-300 3xl:text-base 7xl:text-lg">
            <span className="font-medium">{startItem}</span>
            {' - '}
            <span className="font-medium">{endItem}</span>
            {' / '}
            <span className="font-medium">{totalItems}</span>
            {t('pagination.linksSuffix')}
          </div>

          {/* 페이지당 항목 수 선택 */}
          {onItemsPerPageChange && (
            <div className="flex items-center gap-2 3xl:gap-3">
              <span className="text-sm text-gray-700 dark:text-gray-300 3xl:text-base 7xl:text-lg">
                {t('pagination.perPage')}
              </span>
              <select
                value={itemsPerPage}
                onChange={e => onItemsPerPageChange(Number(e.target.value))}
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white 3xl:px-3 3xl:py-1.5 3xl:text-base 7xl:px-4 7xl:py-2 7xl:text-lg"
                disabled={isLoading}
              >
                <option value={10}>
                  {t('pagination.perPageOption', { count: '10' })}
                </option>
                <option value={20}>
                  {t('pagination.perPageOption', { count: '20' })}
                </option>
                <option value={50}>
                  {t('pagination.perPageOption', { count: '50' })}
                </option>
              </select>
            </div>
          )}
        </div>

        {/* 페이지 네비게이션 */}
        <div className="flex items-center gap-1 3xl:gap-1.5">
          {/* 첫 페이지 */}
          <button
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1 || isLoading}
            className="rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-300 3xl:p-2.5 7xl:p-3"
            title={t('pagination.first')}
          >
            <ChevronDoubleLeftIcon className="h-4 w-4 3xl:h-5 3xl:w-5 7xl:h-6 7xl:w-6" />
          </button>

          {/* 이전 페이지 */}
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1 || isLoading}
            className="rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-300 3xl:p-2.5 7xl:p-3"
            title={t('pagination.prev')}
          >
            <ChevronLeftIcon className="h-4 w-4 3xl:h-5 3xl:w-5 7xl:h-6 7xl:w-6" />
          </button>

          {/* 페이지 번호들 */}
          <div className="flex items-center gap-1 3xl:gap-1.5">
            {generatePageNumbers().map((page, index) => {
              if (page === '...') {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    className="px-3 py-1 text-sm text-gray-500 dark:text-gray-400 3xl:px-4 3xl:py-1.5 3xl:text-base 7xl:px-5 7xl:py-2 7xl:text-lg"
                  >
                    ...
                  </span>
                )
              }

              const pageNumber = page as number
              const isCurrentPage = pageNumber === currentPage

              return (
                <motion.button
                  key={pageNumber}
                  onClick={() => onPageChange(pageNumber)}
                  disabled={isLoading}
                  whileHover={!isCurrentPage ? { scale: 1.05 } : {}}
                  whileTap={!isCurrentPage ? { scale: 0.95 } : {}}
                  className={`rounded-md px-3 py-1 text-sm transition-colors disabled:cursor-not-allowed 3xl:px-4 3xl:py-1.5 3xl:text-base 7xl:px-5 7xl:py-2 7xl:text-lg ${
                    isCurrentPage
                      ? 'bg-blue-600 font-medium text-white'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
                  }`}
                >
                  {pageNumber}
                </motion.button>
              )
            })}
          </div>

          {/* 다음 페이지 */}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages || isLoading}
            className="rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-300 3xl:p-2.5 7xl:p-3"
            title={t('pagination.next')}
          >
            <ChevronRightIcon className="h-4 w-4 3xl:h-5 3xl:w-5 7xl:h-6 7xl:w-6" />
          </button>

          {/* 마지막 페이지 */}
          <button
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages || isLoading}
            className="rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-300 3xl:p-2.5 7xl:p-3"
            title={t('pagination.last')}
          >
            <ChevronDoubleRightIcon className="h-4 w-4 3xl:h-5 3xl:w-5 7xl:h-6 7xl:w-6" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default Pagination
